class ColorSwatches extends HTMLElement {
  constructor() {
    super();
    this.currentProductId = this.dataset.currentProductId;
    this.baseTitle = this.dataset.baseTitle;
    this.productTitle = this.dataset.productTitle;
    this.relatedProducts = [];
    this.linkedProducts = [];
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.linkedProducts = this.getLinkedProducts();
    this.init();
  }

  async init() {
    if (!this.currentProductId) {
      this.hideComponent('Missing currentProductId');
      return;
    }

    if (!this.baseTitle && this.productTitle) {
      const titleParts = this.productTitle.split(' - ');
      if (titleParts.length > 1) {
        this.baseTitle = titleParts.slice(0, -1).join(' - ');
      }
    }

    if (this.baseTitle) {
      try {
        await this.fetchRelatedProducts();
      } catch (error) {
        console.error('Error initializing color swatches:', error);
      }
    }

    this.renderSwatches();
  }

  getLinkedProducts() {
    const node = this.querySelector('[data-linked-products]');
    if (!node) {
      return [];
    }

    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing linked products:', error);
      return [];
    }
  }

  async fetchRelatedProducts() {
    const query = `
      query getRelatedProducts($query: String!) {
        products(first: 50, query: $query) {
          edges {
            node {
              id
              title
              handle
              publishedAt
              onlineStoreUrl
              featuredImage {
                id
                url
                altText
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    availableForSale
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(`https://${this.getShopDomain()}/api/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.getStorefrontToken(),
        },
        body: JSON.stringify({
          query,
          variables: { query: this.baseTitle }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        return;
      }

      this.processGraphQLData(data);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  }

  processGraphQLData(data) {
    if (!data.data.products || !data.data.products.edges) {
      return;
    }

    this.relatedProducts = data.data.products.edges
      .map(edge => edge.node)
      .filter(product => {
        const productTitleParts = product.title.split(' - ');
        const productBaseTitle = productTitleParts.slice(0, -1).join(' - ');
        const productColor = productTitleParts[productTitleParts.length - 1];

        return Boolean(product.publishedAt) &&
               Boolean(product.onlineStoreUrl) &&
               Boolean(product.handle) &&
               productBaseTitle === this.baseTitle &&
               product.id !== this.currentProductId &&
               product.title !== this.productTitle &&
               productColor;
      })
      .map(product => {
        const titleParts = product.title.split(' - ');
        const color = titleParts[titleParts.length - 1];

        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          url: this.buildProductUrl(product.handle),
          color: color,
          swatchImageName: color.toLowerCase().replace(/[\s\/]+/g, '-') + '.png',
          image: product.featuredImage?.url || null,
          available: product.variants.edges[0]?.node.availableForSale || false
        };
      });
  }

  getShopDomain() {
    const hostname = window.location.hostname;
    if (hostname.includes('myshopify.com')) {
      return hostname;
    }
    return window.SHOPIFY_CONFIG?.shopDomain || hostname;
  }

  getStorefrontToken() {
    return window.SHOPIFY_CONFIG?.storefrontToken || '';
  }

  getProductUrlPrefix() {
    const productUrl = (this.dataset.productUrl || window.location.pathname || '').split('?')[0];
    const productsSegment = '/products/';
    const productsIndex = productUrl.indexOf(productsSegment);

    if (productsIndex !== -1) {
      return productUrl.slice(0, productsIndex + productsSegment.length);
    }

    return productsSegment;
  }

  buildProductUrl(handle) {
    return `${this.getProductUrlPrefix()}${handle}`;
  }

  hideComponent(reason) {
    const swatchesContainer = this.querySelector('.color-swatches__container');
    const header = this.querySelector('.color-swatches__header');

    if (swatchesContainer) {
      swatchesContainer.style.display = 'none';
    }

    if (header) {
      header.style.display = 'none';
    }

    this.style.display = 'block';
  }

  renderSwatches() {
    const extraLinkedProducts = this.getUniqueLinkedProducts();

    if (this.relatedProducts.length === 0 && extraLinkedProducts.length === 0) {
      this.hideComponent('No related products found');
      return;
    }

    const container = this.querySelector('.color-swatches__container');
    if (!container) {
      this.hideComponent('Container not found');
      return;
    }

    container.innerHTML = '';
    container.appendChild(this.createCurrentProductSwatch());

    this.relatedProducts.forEach(product => {
      container.appendChild(this.createSwatchElement(product));
    });

    extraLinkedProducts.forEach(product => {
      container.appendChild(this.createSwatchElement(product));
    });
  }

  getUniqueLinkedProducts() {
    const seenHandles = new Set(
      [
        this.dataset.productHandle,
        ...this.relatedProducts.map(product => product.handle)
      ].filter(Boolean).map(handle => handle.toLowerCase())
    );

    return this.linkedProducts.filter(product => {
      const handle = (product.handle || '').toLowerCase();
      if (!handle || !product.url || seenHandles.has(handle)) {
        return false;
      }

      seenHandles.add(handle);
      return true;
    });
  }

  createCurrentProductSwatch() {
    const currentColor = this.productTitle.split(' - ').pop();
    const swatchImageName = currentColor.toLowerCase().replace(/[\s\/]+/g, '-') + '.png';

    const swatchDiv = document.createElement('div');
    swatchDiv.className = 'color-swatch color-swatch--current';
    swatchDiv.dataset.productId = this.currentProductId;

    const link = document.createElement('a');
    link.href = window.location.pathname;
    link.className = 'color-swatch__link';

    const imageDiv = this.createSwatchImage({
      color: currentColor,
      swatchImageName,
      image: this.dataset.featuredImage
    });

    const labelDiv = document.createElement('div');
    labelDiv.className = 'color-swatch__label';
    labelDiv.textContent = currentColor;

    link.appendChild(imageDiv);
    link.appendChild(labelDiv);
    swatchDiv.appendChild(link);

    return swatchDiv;
  }

  createSwatchElement(product) {
    const swatchDiv = document.createElement('div');
    swatchDiv.className = 'color-swatch';
    swatchDiv.dataset.productId = product.id;

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'color-swatch__link';

    const imageDiv = this.createSwatchImage({
      color: product.color,
      swatchImageName: product.swatchImageName,
      image: product.image
    });

    const labelDiv = document.createElement('div');
    labelDiv.className = 'color-swatch__label';
    labelDiv.textContent = product.color;

    link.appendChild(imageDiv);
    link.appendChild(labelDiv);
    swatchDiv.appendChild(link);

    return swatchDiv;
  }

  createSwatchImage({ color, swatchImageName, image }) {
    const imageDiv = document.createElement('div');
    imageDiv.className = 'color-swatch__image';

    const featuredUrl = this.toSquareImageUrl(image);
    const swatchUrl = swatchImageName
      ? `https://${this.getShopDomain()}/cdn/shop/files/${swatchImageName}`
      : '';

    const showPlaceholder = () => {
      imageDiv.replaceChildren();
      const placeholder = document.createElement('div');
      placeholder.className = 'color-swatch__placeholder';
      placeholder.textContent = color.length > 15 ? `${color.substring(0, 15)}...` : color;
      imageDiv.appendChild(placeholder);
    };

    if (!swatchUrl && !featuredUrl) {
      showPlaceholder();
      return imageDiv;
    }

    const img = document.createElement('img');
    img.alt = color;
    img.loading = 'lazy';
    img.width = 100;
    img.height = 100;
    img.className = 'color-swatch__image-asset';

    img.onload = () => {
      img.classList.add('is-loaded');
    };

    img.onerror = () => {
      if (featuredUrl && !img.dataset.fallbackApplied) {
        img.dataset.fallbackApplied = 'true';
        img.src = featuredUrl;
        return;
      }

      showPlaceholder();
    };

    if (swatchUrl) {
      img.src = swatchUrl;
    } else {
      img.dataset.fallbackApplied = 'true';
      img.src = featuredUrl;
    }

    if (img.complete) {
      if (img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.dispatchEvent(new Event('error'));
      }
    }

    imageDiv.appendChild(img);
    return imageDiv;
  }

  toSquareImageUrl(url) {
    if (!url) {
      return '';
    }

    try {
      const absolute = url.startsWith('//') ? `https:${url}` : url;
      const parsed = new URL(absolute, window.location.origin);
      parsed.searchParams.set('width', '200');
      parsed.searchParams.set('height', '200');
      parsed.searchParams.set('crop', 'center');
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }
}

if (!customElements.get('color-swatches')) {
  customElements.define('color-swatches', ColorSwatches);
}

export default ColorSwatches;
